'use client';

import { c as _c } from "react/compiler-runtime";
import { BookingBox, usePaxLabel } from '@7f-ui/components/booking-box/booking-box';
import { Button } from '@7f-ui/components/button/button';
import { Price } from '@7f-ui/components/price';
import { formatDateWithYearShort, useDateLabel } from '@7f-ui/hooks/use-date-label';
import { useMediaState } from '@7f-ui/hooks/use-media-state';
import type { AccommodationPricing } from '@data-access/graphql/schema.types';
import { AccommodationDiscount } from '@feat/feat-accommodation/pricing';
import { useIsCartCurrencyMismatch } from '@feat/feat-cart/currency';
import { trackClickCta } from '@feat/feat-tracking/cta';
import { useUrlState } from '@feat/feat-url-state/use-url-state';
import { WishlistToggle } from '@feat/feat-wishlists/toggle';
import { CalendarBlank, User } from '@phosphor-icons/react';
import { getNightCount } from '@utils/helpers';
import { formatGermanNumber } from '@utils/utils-format';
import { shopTestId } from '@utils/utils-shop-test-ids';
import { differenceInMinutes } from 'date-fns';
import Link from 'next/link';
import { AppLinkLoadingButton } from 'src/app/_page-chrome/link-loading-button';
import type { AccommodationFragment } from 'src/lib/backend/documents/accommodation';
import { AccommodationOfferMicrodata, AccommodationSoldOutMicrodata, type LodgingStay } from 'src/lib/hotel-ads/microdata';
import { HOTEL_ADS_NAV_ORDER } from 'src/lib/hotel-ads/nav';
import { useHotelAdsCriticalPath } from 'src/lib/hotel-ads/use-hotel-ads-critical-path';
import { useAppAccommodationConfigModal } from '../_hooks/app-use-config-modal';
import { type ErrorType, getAccommodationErrorType, getAccommodationRoomsUrl } from '../_lib/accommodation-booking-box';
import { getLodgingStay } from '../_lib/accommodation-detail-query';
import { useAppAccommodationBookingBoxContext } from './app-accommodation-booking-box-provider';
type AppAccommodationBookingBoxDesktopProps = {
  accommodation: AccommodationFragment;
  bestPrice?: AccommodationPricing | null;
  isLoading: boolean;
  isPending: boolean;
  isError: boolean;
};
function getMessage(value?: ErrorType) {
  if (value === 'unavailable') return 'Keine freien Zimmer gefunden. Bitte ändere den Zeitraum.';
  if (value === 'no-date') return 'Preis anzeigen? Wähle einen Zeitraum.';
  return undefined;
}
export function AppAccommodationBookingBoxDesktop(t0) {
  const $ = _c(106);
  const {
    accommodation,
    bestPrice,
    isLoading,
    isPending,
    isError
  } = t0;
  const isMobile = useMediaState("mobile");
  const {
    urlState
  } = useUrlState();
  const {
    setNavigateOnDateSubmit,
    setShowRoomRatesModal
  } = useAppAccommodationBookingBoxContext();
  const {
    setActiveContent
  } = useAppAccommodationConfigModal();
  const isSingleRoom = !!accommodation.room;
  const t1 = urlState.children?.length;
  const t2 = urlState.allocation?.length;
  const t3 = !isSingleRoom;
  let t4;
  if ($[0] !== t1 || $[1] !== t2 || $[2] !== t3 || $[3] !== urlState.adults) {
    t4 = {
      adults: urlState.adults,
      children: t1,
      rooms: t2,
      shorten: false,
      showRooms: t3
    };
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
    $[3] = urlState.adults;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const paxLabel = usePaxLabel(t4);
  let t5;
  if ($[5] !== urlState.from || $[6] !== urlState.to) {
    t5 = {
      fromDate: urlState.from,
      toDate: urlState.to,
      formatFromOptions: formatDateWithYearShort,
      formatToOptions: formatDateWithYearShort
    };
    $[5] = urlState.from;
    $[6] = urlState.to;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const dateLabel = useDateLabel(t5);
  const t6 = !!urlState.from && !!urlState.to;
  const t7 = isLoading || isPending;
  const t8 = !!bestPrice;
  let T0;
  let T1;
  let T2;
  let errorType;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t9;
  if ($[8] !== bestPrice || $[9] !== isError || $[10] !== isLoading || $[11] !== isMobile || $[12] !== isPending || $[13] !== paxLabel || $[14] !== setActiveContent || $[15] !== t6 || $[16] !== t7 || $[17] !== t8 || $[18] !== urlState) {
    errorType = getAccommodationErrorType(t6, t7, t8);
    let t17;
    if ($[31] !== urlState.from) {
      t17 = urlState.from ?? new Date();
      $[31] = urlState.from;
      $[32] = t17;
    } else {
      t17 = $[32];
    }
    let t18;
    if ($[33] !== urlState.to) {
      t18 = urlState.to ?? new Date();
      $[33] = urlState.to;
      $[34] = t18;
    } else {
      t18 = $[34];
    }
    const nights = getNightCount(t17, t18);
    const showTitle = isLoading || isPending || !!bestPrice;
    let t19;
    if ($[35] !== isMobile || $[36] !== urlState) {
      t19 = isMobile ? null : getLodgingStay(urlState);
      $[35] = isMobile;
      $[36] = urlState;
      $[37] = t19;
    } else {
      t19 = $[37];
    }
    const stay = t19;
    t15 = "flex flex-col gap-4";
    if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
      t16 = shopTestId("AccommodationBookingBox.container");
      $[38] = t16;
    } else {
      t16 = $[38];
    }
    T2 = BookingBox;
    t12 = errorType === "unavailable" && !isError && stay && <AccommodationSoldOutMicrodata {...stay} />;
    if ($[39] !== bestPrice || $[40] !== isLoading || $[41] !== isPending || $[42] !== nights || $[43] !== showTitle || $[44] !== stay) {
      t13 = showTitle && <BookingBox.Title isLoading={isLoading || isPending}>{bestPrice?.discountPercentage && bestPrice.originalPrice && <AccommodationDiscount price={bestPrice.originalPrice / nights} currency={bestPrice.currency} discountPercentage={bestPrice.discountPercentage} />}{bestPrice && <div><h3 className="font-heading3" data-testid={shopTestId("AccommodationBookingBox.pricePerNight")}>{"ab "}<Price amount={bestPrice.pricePerNight} currency={bestPrice.currency} />{" "}<span className="font-body-small font-normal">pro Nacht</span></h3><p className="font-body-small text-secondary-text" data-testid={shopTestId("AccommodationBookingBox.totalPrice")}>{"ab "}<StayTotalPrice pricing={bestPrice} stay={stay} />{" f\xFCr "}{nights === 1 ? "1 Nacht" : <>{formatGermanNumber(nights)}{" N\xE4chte"}</>}</p></div>}</BookingBox.Title>;
      $[39] = bestPrice;
      $[40] = isLoading;
      $[41] = isPending;
      $[42] = nights;
      $[43] = showTitle;
      $[44] = stay;
      $[45] = t13;
    } else {
      t13 = $[45];
    }
    let t20;
    if ($[46] !== setActiveContent) {
      t20 = () => {
        setActiveContent("Pax");
        trackClickCta("person selector", {
          type: "accommodation"
        });
      };
      $[46] = setActiveContent;
      $[47] = t20;
    } else {
      t20 = $[47];
    }
    let t21;
    if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
      t21 = shopTestId("AccommodationBookingBox.paxButton");
      $[48] = t21;
    } else {
      t21 = $[48];
    }
    if ($[49] !== paxLabel || $[50] !== t20) {
      t14 = <BookingBox.Field label="Personen"><BookingBox.Button Icon={User} onClick={t20} data-testid={t21}>{paxLabel}</BookingBox.Button></BookingBox.Field>;
      $[49] = paxLabel;
      $[50] = t20;
      $[51] = t14;
    } else {
      t14 = $[51];
    }
    T1 = BookingBox.Field;
    t11 = "Reisezeitraum";
    T0 = BookingBox.Button;
    t9 = errorType === "unavailable";
    t10 = getMessage(errorType);
    $[8] = bestPrice;
    $[9] = isError;
    $[10] = isLoading;
    $[11] = isMobile;
    $[12] = isPending;
    $[13] = paxLabel;
    $[14] = setActiveContent;
    $[15] = t6;
    $[16] = t7;
    $[17] = t8;
    $[18] = urlState;
    $[19] = T0;
    $[20] = T1;
    $[21] = T2;
    $[22] = errorType;
    $[23] = t10;
    $[24] = t11;
    $[25] = t12;
    $[26] = t13;
    $[27] = t14;
    $[28] = t15;
    $[29] = t16;
    $[30] = t9;
  } else {
    T0 = $[19];
    T1 = $[20];
    T2 = $[21];
    errorType = $[22];
    t10 = $[23];
    t11 = $[24];
    t12 = $[25];
    t13 = $[26];
    t14 = $[27];
    t15 = $[28];
    t16 = $[29];
    t9 = $[30];
  }
  let t17;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = shopTestId("AccommodationBookingBox.dateButton");
    $[52] = t17;
  } else {
    t17 = $[52];
  }
  let t18;
  if ($[53] !== setActiveContent || $[54] !== setNavigateOnDateSubmit) {
    t18 = () => {
      setNavigateOnDateSubmit(false);
      setActiveContent("Date");
      trackClickCta("calendar", {
        type: "accommodation"
      });
    };
    $[53] = setActiveContent;
    $[54] = setNavigateOnDateSubmit;
    $[55] = t18;
  } else {
    t18 = $[55];
  }
  let t19;
  if ($[56] !== T0 || $[57] !== dateLabel || $[58] !== t10 || $[59] !== t18 || $[60] !== t9) {
    t19 = <T0 isError={t9} message={t10} Icon={CalendarBlank} data-testid={t17} onClick={t18}>{dateLabel}</T0>;
    $[56] = T0;
    $[57] = dateLabel;
    $[58] = t10;
    $[59] = t18;
    $[60] = t9;
    $[61] = t19;
  } else {
    t19 = $[61];
  }
  let t20;
  if ($[62] !== T1 || $[63] !== t11 || $[64] !== t19) {
    t20 = <T1 label={t11}>{t19}</T1>;
    $[62] = T1;
    $[63] = t11;
    $[64] = t19;
    $[65] = t20;
  } else {
    t20 = $[65];
  }
  let t21;
  if ($[66] !== setActiveContent || $[67] !== setNavigateOnDateSubmit) {
    t21 = () => {
      setActiveContent("Date");
      setNavigateOnDateSubmit(true);
      trackClickCta("calendar", {
        type: "accommodation"
      });
    };
    $[66] = setActiveContent;
    $[67] = setNavigateOnDateSubmit;
    $[68] = t21;
  } else {
    t21 = $[68];
  }
  let t22;
  if ($[69] !== setShowRoomRatesModal) {
    t22 = () => {
      setShowRoomRatesModal(true);
      trackClickCta("to cart", {
        type: "accommodation"
      });
    };
    $[69] = setShowRoomRatesModal;
    $[70] = t22;
  } else {
    t22 = $[70];
  }
  let t23;
  if ($[71] !== accommodation.currency || $[72] !== accommodation.id || $[73] !== accommodation.slug || $[74] !== errorType || $[75] !== isSingleRoom || $[76] !== t21 || $[77] !== t22) {
    t23 = <div className="grow"><CTA id={accommodation.id} slug={accommodation.slug} currency={accommodation.currency} isSingleRoom={isSingleRoom} errorType={errorType} onOpenDate={t21} onOpenRoomRates={t22} /></div>;
    $[71] = accommodation.currency;
    $[72] = accommodation.id;
    $[73] = accommodation.slug;
    $[74] = errorType;
    $[75] = isSingleRoom;
    $[76] = t21;
    $[77] = t22;
    $[78] = t23;
  } else {
    t23 = $[78];
  }
  let t24;
  if ($[79] !== accommodation.id) {
    t24 = accommodation.id.toString();
    $[79] = accommodation.id;
    $[80] = t24;
  } else {
    t24 = $[80];
  }
  const t25 = accommodation.images?.[0]?.url;
  let t26;
  if ($[81] !== urlState.from || $[82] !== urlState.to) {
    t26 = urlState.to && urlState.from ? differenceInMinutes(urlState.to, urlState.from) : undefined;
    $[81] = urlState.from;
    $[82] = urlState.to;
    $[83] = t26;
  } else {
    t26 = $[83];
  }
  let t27;
  if ($[84] !== accommodation.city || $[85] !== accommodation.id || $[86] !== accommodation.name || $[87] !== accommodation.stars || $[88] !== t24 || $[89] !== t25 || $[90] !== t26) {
    t27 = <div className="flex-none"><WishlistToggle productId={t24} publicProductId={accommodation.id} productType="accommodation" productName={accommodation.name} locationName={accommodation.city} image={t25} stars={accommodation.stars} duration={t26} origin="pdp" /></div>;
    $[84] = accommodation.city;
    $[85] = accommodation.id;
    $[86] = accommodation.name;
    $[87] = accommodation.stars;
    $[88] = t24;
    $[89] = t25;
    $[90] = t26;
    $[91] = t27;
  } else {
    t27 = $[91];
  }
  let t28;
  if ($[92] !== t23 || $[93] !== t27) {
    t28 = <div className="flex items-center gap-4">{t23}{t27}</div>;
    $[92] = t23;
    $[93] = t27;
    $[94] = t28;
  } else {
    t28 = $[94];
  }
  let t29;
  if ($[95] !== T2 || $[96] !== t12 || $[97] !== t13 || $[98] !== t14 || $[99] !== t20 || $[100] !== t28) {
    t29 = <T2>{t12}{t13}{t14}{t20}{t28}</T2>;
    $[95] = T2;
    $[96] = t12;
    $[97] = t13;
    $[98] = t14;
    $[99] = t20;
    $[100] = t28;
    $[101] = t29;
  } else {
    t29 = $[101];
  }
  let t30;
  if ($[102] !== t15 || $[103] !== t16 || $[104] !== t29) {
    t30 = <div className={t15} data-testid={t16}>{t29}</div>;
    $[102] = t15;
    $[103] = t16;
    $[104] = t29;
    $[105] = t30;
  } else {
    t30 = $[105];
  }
  return t30;
}
type StayTotalPriceProps = {
  pricing: AccommodationPricing;
  stay: LodgingStay | null;
};
function StayTotalPrice(t0) {
  const $ = _c(7);
  const {
    pricing,
    stay
  } = t0;
  const t1 = !!stay;
  let t2;
  if ($[0] !== pricing.currency || $[1] !== pricing.price || $[2] !== t1) {
    t2 = <Price amount={pricing.price} currency={pricing.currency} microdata={t1} />;
    $[0] = pricing.currency;
    $[1] = pricing.price;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const price = t2;
  if (!stay) {
    return price;
  }
  let t3;
  if ($[4] !== price || $[5] !== stay) {
    t3 = <AccommodationOfferMicrodata {...stay}>{price}</AccommodationOfferMicrodata>;
    $[4] = price;
    $[5] = stay;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
type CTAProps = {
  id: number;
  slug: string;
  currency: string;
  isSingleRoom: boolean;
  errorType: ErrorType | undefined;
  onOpenDate: () => void;
  onOpenRoomRates: () => void;
};
function CTA(t0) {
  const $ = _c(14);
  const {
    id,
    slug,
    currency,
    isSingleRoom,
    errorType,
    onOpenDate,
    onOpenRoomRates
  } = t0;
  const isCurrencyMismatch = useIsCartCurrencyMismatch(currency);
  const shouldUseButton = isSingleRoom || errorType === "no-date";
  const navAttributes = useHotelAdsCriticalPath(HOTEL_ADS_NAV_ORDER.startBooking, "desktop", !errorType);
  const ctaLabel = isSingleRoom ? "In den Warenkorb" : "Zimmer w\xE4hlen";
  let t1;
  if ($[0] !== errorType || $[1] !== isSingleRoom || $[2] !== onOpenDate || $[3] !== onOpenRoomRates) {
    t1 = () => {
      if (errorType === "no-date") {
        onOpenDate();
        return;
      }
      if (isSingleRoom && !errorType) {
        onOpenRoomRates();
        return;
      }
      trackClickCta("choose rooms", {
        type: "accommodation"
      });
    };
    $[0] = errorType;
    $[1] = isSingleRoom;
    $[2] = onOpenDate;
    $[3] = onOpenRoomRates;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const handleClick = t1;
  let t2;
  if ($[5] !== ctaLabel || $[6] !== errorType || $[7] !== handleClick || $[8] !== id || $[9] !== isCurrencyMismatch || $[10] !== navAttributes || $[11] !== shouldUseButton || $[12] !== slug) {
    t2 = shouldUseButton ? <Button full={true} disabled={!!errorType && errorType !== "no-date" || isCurrencyMismatch} onClick={handleClick} {...navAttributes}>{ctaLabel}</Button> : <AppLinkLoadingButton full={true} disabled={!!errorType || isCurrencyMismatch} as={Link} href={getAccommodationRoomsUrl(id, slug)} onClick={handleClick} {...navAttributes}>{ctaLabel}</AppLinkLoadingButton>;
    $[5] = ctaLabel;
    $[6] = errorType;
    $[7] = handleClick;
    $[8] = id;
    $[9] = isCurrencyMismatch;
    $[10] = navAttributes;
    $[11] = shouldUseButton;
    $[12] = slug;
    $[13] = t2;
  } else {
    t2 = $[13];
  }
  return t2;
}